import { useId, useState } from 'react';
import { ROLES, type NotificationRule, type Role } from '@/domain';
import {
  useNotificationSettings,
  useResetRoleNotifications,
  useResetUserNotifications,
  useSaveRoleNotifications,
  useSaveUserNotifications,
  useUserNotifications,
} from '@/application/usecases/useNotifications';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { toast } from '@/ui/primitives/toastStore';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { NotificationRulesEditor } from './NotificationRulesEditor';
import styles from './NotificationSettings.module.css';

const text = strings.notifications.admin;

/**
 * Who is notified of what, a role at a time. The request of 7 October 2026,
 * section 1, step 3: on Users, management sets for each role which
 * notifications it receives, where they appear, and which are mandatory.
 *
 * Saving asks once, with the count, and Reset to defaults asks too. Every
 * change is a row in the activity log, written by the server: who changed what
 * for which role, and when.
 */
export function RoleNotificationSettings() {
  const roleId = useId();
  const [role, setRole] = useState<Role>(ROLES[0]);
  const settings = useNotificationSettings();
  const save = useSaveRoleNotifications();
  const reset = useResetRoleNotifications();
  const ask = useConfirmAction();

  if (settings.isPending) return <SkeletonRows rows={6} label={text.loading} />;
  if (settings.isError) return <p className={styles.hint}>{strings.errors.server}</p>;

  const of = settings.data.roles.find((each) => each.role === role);
  const label = of?.label ?? role;

  const saveRules = (rules: NotificationRule[]) =>
    ask(text.confirmSave(rules.length, label), () =>
      save.mutateAsync({ role, rules }).then(() => {
        toast(text.saved(label), { tone: 'success' });
      }),
    );

  const resetRules = () =>
    ask(text.confirmReset(label), () =>
      reset.mutateAsync(role).then(() => {
        toast(text.wasReset(label), { tone: 'success' });
      }),
    );

  return (
    <div>
      <div className={styles.head}>
        <h3>{text.title}</h3>
      </div>
      <p className={styles.hint}>{text.hint}</p>

      <div className={styles.role}>
        <label htmlFor={roleId}>{text.role}</label>
        <select id={roleId} value={role} onChange={(event) => setRole(event.target.value as Role)}>
          {settings.data.roles.map((each) => (
            <option key={each.role} value={each.role}>
              {each.label}
            </option>
          ))}
        </select>
      </div>

      <NotificationRulesEditor
        // Another role is another draft: nothing typed for one may be saved for the next.
        key={role}
        categories={settings.data.categories}
        types={settings.data.types}
        rules={of?.rules ?? []}
        changedMark={text.changedMark}
        onSave={saveRules}
        onReset={resetRules}
        resetLabel={text.reset}
        busy={save.isPending || reset.isPending}
      />
    </div>
  );
}

/**
 * One person's notifications, on their record. The same request: management
 * adjusts the settings for an individual, overriding their role's, and Reset
 * to role defaults takes every exception off them.
 */
export function UserNotificationSettings({ userId, name }: { userId: string; name: string }) {
  const settings = useNotificationSettings();
  const mine = useUserNotifications(userId);
  const save = useSaveUserNotifications();
  const reset = useResetUserNotifications();
  const ask = useConfirmAction();

  if (settings.isPending || mine.isPending) return <SkeletonRows rows={4} label={text.loading} />;
  if (settings.isError || mine.isError) return <p className={styles.hint}>{strings.errors.server}</p>;

  const saveRules = (rules: NotificationRule[]) =>
    ask(text.confirmSave(rules.length, name), () =>
      save.mutateAsync({ userId, rules }).then(() => {
        toast(text.saved(name), { tone: 'success' });
      }),
    );

  const resetRules = () =>
    ask(text.confirmResetUser(name, mine.data.roleLabel), () =>
      reset.mutateAsync(userId).then(() => {
        toast(text.wasReset(name), { tone: 'success' });
      }),
    );

  return (
    <section className={styles.card} aria-label={text.userTitle}>
      <div className={styles.head}>
        <h3>{text.userTitle}</h3>
      </div>
      <p className={styles.hint}>{text.userHint(mine.data.roleLabel)}</p>

      <NotificationRulesEditor
        categories={settings.data.categories}
        types={settings.data.types}
        rules={mine.data.rules}
        changedMark={text.ownMark}
        onSave={saveRules}
        onReset={resetRules}
        resetLabel={text.resetUser}
        busy={save.isPending || reset.isPending}
      />
    </section>
  );
}
